import React, { useState, useEffect, useContext } from 'react';
import { AuthContext } from '../App';
import axios from 'axios';
import { Loader2 } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';

const STAGE_COLORS = ['#4f46e5', '#0891b2', '#059669'];

const FunnelBars = ({ stages, testidPrefix }) => {
  const max = stages[0]?.value || 1;
  return (
    <div className="space-y-3" data-testid={testidPrefix}>
      {stages.map((s, idx) => {
        const widthPct = Math.max((s.value / max) * 100, s.value > 0 ? 8 : 2);
        const pctOfFirst = max > 0 ? Math.round((s.value / max) * 100) : 0;
        return (
          <div key={s.stage} data-testid={`${testidPrefix}-stage-${idx}`}>
            <div className="flex items-center justify-between mb-1">
              <span className="text-sm font-medium text-gray-700">{s.stage}</span>
              <span className="text-sm font-semibold text-gray-900">{s.value}{idx > 0 && <span className="text-xs text-gray-400 ml-1.5">({pctOfFirst}% of registered)</span>}</span>
            </div>
            <div className="h-8 bg-gray-100 rounded-md overflow-hidden">
              <div
                className="h-full rounded-md flex items-center transition-all duration-500"
                style={{ width: `${widthPct}%`, backgroundColor: STAGE_COLORS[idx % STAGE_COLORS.length] }}
              />
            </div>
          </div>
        );
      })}
    </div>
  );
};

const RateChip = ({ label, value }) => (
  <div className="rounded-lg bg-gray-50 border border-gray-200 px-3 py-2 text-center">
    <p className="text-lg font-bold text-gray-900">{value}%</p>
    <p className="text-[11px] text-gray-500">{label}</p>
  </div>
);

const ConversionFunnelChart = () => {
  const { API, language } = useContext(AuthContext);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchFunnel = async () => {
      try {
        const token = localStorage.getItem('helply_token');
        const res = await axios.get(`${API}/admin/statistics/funnel`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setData(res.data);
      } catch (error) {
        console.error('Failed to load conversion funnel:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchFunnel();
  }, [API]);

  const t = language === 'de' ? {
    title: 'Konversionstrichter', clients: 'Kunden: Registrierung → Abo', pros: 'Fachkräfte: Registrierung → Verifiziert → Abo'
  } : language === 'fr' ? {
    title: 'Entonnoir de Conversion', clients: 'Clients : Inscription → Abonnement', pros: 'Pros : Inscription → Vérifié → Abonnement'
  } : language === 'it' ? {
    title: 'Funnel di Conversione', clients: 'Clienti: Registrazione → Abbonamento', pros: 'Professionisti: Registrazione → Verificato → Abbonamento'
  } : {
    title: 'Conversion Funnel', clients: 'Clients: Registered → Subscribed', pros: 'Professionals: Registered → Verified → Subscribed'
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-10" data-testid="funnel-loading">
        <Loader2 className="w-6 h-6 text-indigo-600 animate-spin" />
      </div>
    );
  }
  if (!data) return null;

  const rates = data.rates || {};

  return (
    <Card data-testid="conversion-funnel-card">
      <CardHeader>
        <CardTitle className="text-base">{t.title}</CardTitle>
      </CardHeader>
      <CardContent className="space-y-6">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <RateChip label="Client conv." value={rates.client_conversion_pct ?? 0} />
          <RateChip label="Pro verified" value={rates.pro_verification_pct ?? 0} />
          <RateChip label="Pro conv." value={rates.pro_overall_conversion_pct ?? 0} />
          <RateChip label="Overall conv." value={rates.overall_conversion_pct ?? 0} />
        </div>

        <div className="grid md:grid-cols-2 gap-8">
          <div>
            <p className="text-xs font-medium text-gray-500 mb-3">{t.clients}</p>
            <FunnelBars stages={data.client_funnel || []} testidPrefix="client-funnel" />
          </div>
          <div>
            <p className="text-xs font-medium text-gray-500 mb-3">{t.pros}</p>
            <FunnelBars stages={data.professional_funnel || []} testidPrefix="pro-funnel" />
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

export default ConversionFunnelChart;
